import { Plugin } from 'ckeditor5/src/core';
import { LinkEditing } from '@ckeditor/ckeditor5-link';
import SmartButtonUI from './smartbuttonui';
import SmartButtonCommand from './smartbuttoncommand';
import type { ViewElement } from 'ckeditor5/src/engine';

// Import the CSS for styling
import '../../theme/smartbutton.css';

export default class SmartButton extends Plugin {
	/**
     * @inheritDoc
     */
	public static get pluginName() {
		return 'SmartButton' as const;
	}

	/**
     * @inheritDoc
     */
	public static get requires() {
		return [ SmartButtonUI, LinkEditing ] as const;
	}

	/**
     * @inheritDoc
     */
	public init(): void {
		const editor = this.editor;

		// Define schema
		this._defineSchema();

		// Define converters
		this._defineConverters();

		// Register the command
		editor.commands.add(
			SmartButtonCommand.COMMAND_NAME,
			new SmartButtonCommand( editor )
		);

		// Register content matcher to preserve attributes
		editor.data.registerRawContentMatcher( {
			name: 'a',
			classes: [ 'smart-button', 'smart-button-centered', 'smart-button-right' ],
			attributes: [ 'href', 'target', 'style' ]
		} );
	}

	/**
     * Defines the smart button schema.
     */
	private _defineSchema(): void {
		const schema = this.editor.model.schema;

		// Extend the text schema to allow smart button attributes
		schema.extend( '$text', {
			allowAttributes: [
				'smartButton',
				'smartButtonAlignment'
			]
		} );
	}

	/**
     * Defines converters for the smart button.
     */
	private _defineConverters(): void {
		const conversion = this.editor.conversion;
		const linkEditing = this.editor.plugins.get( LinkEditing );
		conversion.for( 'downcast' ).add( dispatcher => {
			dispatcher.on( 'attribute:linkHref', ( evt, data, conversionApi ) => {
				const { item } = data;
				if ( item.hasAttribute( 'smartButton' ) ) {
					evt.stop();
				}
			}, { priority: 'high' } );
		} );

		conversion.for( 'downcast' ).attributeToElement( {
			model: 'smartButton',
			view: ( smartButtonValue, { writer }, { item } ) => {
				if ( !smartButtonValue ) {
					return null;
				}

				const alignment = String( item.getAttribute( 'smartButtonAlignment' ) || 'none' );
				const href = String( item.getAttribute( 'linkHref' ) || '' );
				const target = item.hasAttribute( 'linkTarget' ) ? '_blank' : '_self';

				let buttonClass = 'smart-button';
				if ( alignment === 'center' ) {
					buttonClass = 'smart-button smart-button-centered';
				} else if ( alignment === 'right' ) {
					buttonClass = 'smart-button smart-button-right';
				}

				const buttonAttributes: Record<string, string> = {
					class: buttonClass,
					href,
					target
				};

				const buttonElement = writer.createAttributeElement( 'a', buttonAttributes, {
					priority: 25, // Higher than link (20) to ensure our converter takes precedence
					id: 'smart-button'
				} );

				if ( alignment === 'center' ) {
					writer.setStyle( {
						'display': 'block',
						'width': 'fit-content',
						'margin-left': 'auto',
						'margin-right': 'auto',
						'text-align': 'center'
					}, buttonElement );
				} else if ( alignment === 'left' ) {
					writer.setStyle( 'float', 'left', buttonElement );
				}

				return buttonElement;
			},
			converterPriority: 'highest'
		} );

		// UPCAST CONVERTERS (view → model)

		// Base Smart Button class -> smartButton attribute
		conversion.for( 'upcast' ).elementToAttribute( {
			view: {
				name: 'a',
				classes: [ 'smart-button' ]
			},
			model: {
				key: 'smartButton',
				value: true
			},
			converterPriority: 'high'
		} );

		// Center alignment class -> smartButtonAlignment attribute
		conversion.for( 'upcast' ).elementToAttribute( {
			view: {
				name: 'a',
				classes: [ 'smart-button-centered' ]
			},
			model: {
				key: 'smartButtonAlignment',
				value: 'center'
			},
			converterPriority: 'high'
		} );

		// Right alignment class -> smartButtonAlignment attribute
		conversion.for( 'upcast' ).elementToAttribute( {
			view: {
				name: 'a',
				classes: [ 'smart-button-right' ]
			},
			model: {
				key: 'smartButtonAlignment',
				value: 'right'
			},
			converterPriority: 'high'
		} );

		// Center alignment by style (fallback for backward compatibility)
		conversion.for( 'upcast' ).elementToAttribute( {
			view: ( element: ViewElement ) => {
				if ( element.name === 'a' &&
                    element.hasClass( 'smart-button' ) &&
                    element.getStyle( 'display' ) === 'block' &&
                    ( element.getStyle( 'margin-left' ) === 'auto' ||
                     element.getStyle( 'margin-right' ) === 'auto' ) ) {
					return { name: true };
				}
				return null;
			},
			model: {
				key: 'smartButtonAlignment',
				value: 'center'
			},
			converterPriority: 'high'
		} );

		// Right alignment by style (fallback for backward compatibility)
		conversion.for( 'upcast' ).elementToAttribute( {
			view: ( element: ViewElement ) => {
				if ( element.name === 'a' &&
                    element.hasClass( 'smart-button' ) &&
                    element.getStyle( 'float' ) === 'right' ) {
					return { name: true };
				}
				return null;
			},
			model: {
				key: 'smartButtonAlignment',
				value: 'right'
			},
			converterPriority: 'high'
		} );
	}
}
